import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ReactComponent as CopyIcon } from '../../../assets/icons/duplicate.svg';
import { ReactComponent as CheckIcon } from '../../../assets/icons/check.svg';
import { formatDate, formatPrice } from '../../../utils/entityUtils';
import type { Chiffrage, ChiffrageCouple } from '../chiffrageTypes';

// Menu de reprise d'un chiffrage. Ce n'est PAS un <select> natif : chaque entree tient sur deux lignes
// (libelle, puis rappel synthetique) et porte un bouton de copie du code — deux choses qu'un <option>,
// qui ne rend que du texte plat, ne sait pas faire. On reimplemente donc le strict necessaire :
// declencheur + panneau, fermeture au clic exterieur et a Echap.
//
// Le declencheur n'affiche QUE le libelle du chiffrage courant (on voit sur lequel on est) ; le detail
// ne vit que dans le panneau. Aucune option de deselection : on ne peut que basculer d'un chiffrage a
// un autre.

interface ChiffrageEnCoursSelectProps {
  chiffrages: Chiffrage[];
  couples: ChiffrageCouple[];
  /** Prix total resolu a la volee, par id de chiffrage (aucun prix n'est stocke). */
  totals: Map<number, number>;
  currentId: number | null;
  onSelect: (id: number) => void;
}

const coupleKey = (idLotTechnique: number | string, idDepartement: number | string) =>
  `${Number(idLotTechnique)}:${Number(idDepartement)}`;

const ChiffrageEnCoursSelect: React.FC<ChiffrageEnCoursSelectProps> = ({
  chiffrages,
  couples,
  totals,
  currentId,
  onSelect,
}) => {
  const [open, setOpen] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const coupleByKey = useMemo(() => {
    const map = new Map<string, ChiffrageCouple>();
    couples.forEach((c) => map.set(coupleKey(c.id_lot_technique, c.id_departement), c));
    return map;
  }, [couples]);

  // Fermeture au clic exterieur + Echap : un panneau ouvert ne doit pas rester coince.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Meme accuse de reception que la liste des chiffrages : l'icone passe au "check" 2s.
  const handleCopy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      window.setTimeout(() => setCopiedCode(null), 2000);
    } catch {
      /* Presse-papiers indisponible : le code reste lisible a l'ecran, on n'interrompt pas. */
    }
  };

  const current = chiffrages.find((c) => c.id === currentId) ?? null;
  const isEmpty = chiffrages.length === 0;

  const labelOf = (c: Chiffrage) => (c.libelle && c.libelle.trim() ? c.libelle.trim() : `Chiffrage n° ${c.id}`);

  // Ligne 2 : date, lots AVEC LEUR NOM, puis le contrat. Chaque element est omis s'il est inconnu
  // (plutot qu'un tiret sans valeur informative).
  const lotsLine = (c: Chiffrage): string => {
    const couple = coupleByKey.get(coupleKey(c.id_lot_technique, c.id_departement));
    return [
      c.created_at ? formatDate(c.created_at, false) : null,
      couple ? `${couple.lot_code} - ${couple.lot_nom}` : String(c.id_lot_technique),
      couple ? `${couple.id_departement} - ${couple.departement_nom}` : String(c.id_departement),
      couple?.contrat_numero ? `Contrat N° : ${couple.contrat_numero}` : null,
    ]
      .filter(Boolean)
      .join(' - ');
  };

  // Ligne 3 : les chiffres (nombre d'articles + prix total resolu a la volee), et l'etat du chiffrage
  // — un brouillon n'a pas de code, le dire vaut mieux que laisser un blanc a interpreter.
  const figuresLine = (c: Chiffrage): string => {
    const total = totals.get(c.id);
    const nb = c.articles_count ?? c.articles?.length;
    return [
      nb != null ? `${nb} article${nb > 1 ? 's' : ''}` : null,
      total != null ? formatPrice(total, 2) : null,
      c.code ? null : 'Brouillon',
    ]
      .filter(Boolean)
      .join(' - ');
  };

  return (
    <div className="chiffrage-encours" ref={rootRef}>
      <button
        type="button"
        className="chiffrage-encours-trigger"
        disabled={isEmpty}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {/* Le declencheur porte tout ce que porte l'option (nom, date, lots, contrat, articles, prix,
            code) mais sur UNE ligne : il doit garder la hauteur des boutons de la barre. Le nom reste
            en gras, le reste suit en gris — et l'ellipse tronque proprement si la barre est etroite.
            N'y montrer que le libelle obligeait a rouvrir le menu pour savoir sur quel chiffrage on
            etait ; l'empiler sur trois lignes cassait l'alignement de la barre. */}
        {isEmpty || !current ? (
          <span className="chiffrage-encours-trigger-text">
            {isEmpty ? 'Aucun chiffrage en cours' : 'Afficher un chiffrage en cours'}
          </span>
        ) : (
          <span className="chiffrage-encours-trigger-text">
            <span className="chiffrage-encours-option-nom">{labelOf(current)}</span>
            {/* Le code sort de la chaine jointe : noye dedans, il ne pouvait pas etre mis en avant.
                C'est l'information que le presta vient chercher pour la communiquer — elle merite son
                propre element, en gras. */}
            <span className="chiffrage-encours-trigger-suite">
              {[lotsLine(current), figuresLine(current)].filter(Boolean).join(' - ')}
              {current.code && (
                <>
                  {' - '}
                  <strong className="chiffrage-encours-trigger-code">Code : {current.code}</strong>
                </>
              )}
            </span>
          </span>
        )}
        {/* Chevron (meme trace que les icones de tri du tableau), retourne vers le haut a l'ouverture. */}
        <svg
          className={`chiffrage-encours-chevron ${open ? 'chiffrage-encours-chevron--open' : ''}`}
          width="10"
          height="6"
          viewBox="0 0 8 5"
          fill="none"
          aria-hidden="true"
        >
          <path d="M1.28571 0.5L4.28571 3.5L7.28571 0.5" stroke="currentColor" strokeLinecap="round" />
        </svg>
      </button>

      {open && !isEmpty && (
        <ul className="chiffrage-encours-panel" role="listbox">
          {chiffrages.map((c) => {
            const justCopied = copiedCode === c.code;
            const Icon = justCopied ? CheckIcon : CopyIcon;
            return (
              <li
                key={c.id}
                role="option"
                aria-selected={c.id === currentId}
                className={`chiffrage-encours-item ${c.id === currentId ? 'chiffrage-encours-item--current' : ''}`}
              >
                <button
                  type="button"
                  className="chiffrage-encours-option"
                  onClick={() => {
                    setOpen(false);
                    if (c.id !== currentId) onSelect(c.id);
                  }}
                >
                  <span className="chiffrage-encours-option-nom">{labelOf(c)}</span>
                  <span className="chiffrage-encours-option-details">{lotsLine(c)}</span>
                  <span className="chiffrage-encours-option-details">{figuresLine(c)}</span>
                </button>

                {/* Bouton FRERE de l'option (et non imbrique : un <button> dans un <button> est du
                    HTML invalide). Il ne doit pas declencher la selection. */}
                {c.code && (
                  <button
                    type="button"
                    className="chiffrage-encours-copy"
                    title={justCopied ? 'Code copié' : 'Copier le code'}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopy(c.code as string);
                    }}
                  >
                    <span className="chiffrage-encours-code">{c.code}</span>
                    {/* Classe distincte par etat : l'icone de copie est repeinte en blanc (son fill
                        est sombre dans le SVG), le check garde son vert natif. */}
                    <Icon className={justCopied ? 'chiffrage-encours-check' : 'chiffrage-encours-copy-icon'} />
                    {/* Confirmation lisible : une icone seule ne dit pas que c'est copie. */}
                    {justCopied && <span className="chiffrage-encours-copie">Copié</span>}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ChiffrageEnCoursSelect;
